import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {generatePath, useNavigate, useSearchParams} from "react-router-dom";
import {useMutation} from "@tanstack/react-query";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {CircularProgress, Stack, Typography} from "@mui/material";
import Page from "../../../common/page/Page";
import {DASHBOARD, SUBMISSION_AUTHORIZATION_RESULT} from "../../../AppRoutes";
import {DigitalActionAuthorizationCompletionResultDto} from "../../../client/submission/SubmissionAuthorizationApiClient";
import {
    authorizationRetry,
    AUTHORIZATION_RETRY_DELAY_MS,
    isAuthorizationServiceUnavailableError,
    isSubmissionNotReadyForAuthorizationError
} from "./AuthorizationRetryPolicy";
import AuthorizationServiceUnavailableDialog from "./AuthorizationServiceUnavailableDialog";
import SubmissionNotReadyForAuthorizationDialog from "./SubmissionNotReadyForAuthorizationDialog";


export enum SubmissionAuthorizationCompletionPageParam {
    DIGITAL_ACTION_ID_PARAM = "Id",
    RESULT_PARAM = "Result",
    ERROR_CODE_PARAM = "ErrorCode",
}

interface CompletionMutationVariables {
    digitalActionId: string;
    result: string;
    errorCode: string | null;
}

export interface SubmissionAuthorizationCompletionPageProps {
}

export function SubmissionAuthorizationCompletionPage(props: Readonly<SubmissionAuthorizationCompletionPageProps>) {
    const {t} = useTranslation();
    const { submissionAuthorizationClient } = useClientLocator();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const initialized = useRef(false);
    const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
    const [showNotReadyDialog, setShowNotReadyDialog] = useState(false);

    const mutation = useMutation<DigitalActionAuthorizationCompletionResultDto | undefined, unknown, CompletionMutationVariables>({
        // Retries reuse the exact same digitalActionId/result/errorCode (a re-poll of the same
        // NIA outcome) - the submission stays AUTHORIZATION_IN_PROGRESS on the BE side during a
        // temporary (503) failure, so the user must not be asked to authorize again in NIA.
        mutationFn: (vars) => submissionAuthorizationClient.completeDigitalActionAuthorization(vars.digitalActionId, vars.result, vars.errorCode),
        retry: authorizationRetry,
        retryDelay: AUTHORIZATION_RETRY_DELAY_MS,
        onSuccess: (response) => {
            if (response) {
                const authResultPath = generatePath(SUBMISSION_AUTHORIZATION_RESULT,
                    { submissionId: String(response.submissionId) })
                navigate(authResultPath);
            } else {
                navigate(DASHBOARD);
            }
        },
        onError: (err) => {
            if (isAuthorizationServiceUnavailableError(err)) {
                setShowUnavailableDialog(true);
            } else if (isSubmissionNotReadyForAuthorizationError(err)) {
                setShowNotReadyDialog(true);
            } else {
                navigate(DASHBOARD);
            }
        },
    });

    const {mutate} = mutation;

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;

            const digitalActionId = searchParams.get(SubmissionAuthorizationCompletionPageParam.DIGITAL_ACTION_ID_PARAM);
            const result = searchParams.get(SubmissionAuthorizationCompletionPageParam.RESULT_PARAM);
            const errorCode = searchParams.get(SubmissionAuthorizationCompletionPageParam.ERROR_CODE_PARAM);

            if (digitalActionId && result) {
                mutate({digitalActionId, result, errorCode});
            } else {
                navigate(DASHBOARD);
            }
        }
    }, [searchParams, navigate, mutate]);

    if (showUnavailableDialog) {
        return (
            <Page title={t(SubmissionTranslKey.SUBMISSION_AUTHORIZATION_COMPLETION)}>
                <AuthorizationServiceUnavailableDialog open={true} onClose={() => navigate(DASHBOARD)} />
            </Page>
        );
    }

    if (showNotReadyDialog) {
        return (
            <Page title={t(SubmissionTranslKey.SUBMISSION_AUTHORIZATION_COMPLETION)}>
                <SubmissionNotReadyForAuthorizationDialog open={true} onClose={() => navigate(DASHBOARD)} />
            </Page>
        );
    }

    const retrying = mutation.failureCount > 0;

    return (
        <Page title={t(SubmissionTranslKey.SUBMISSION_AUTHORIZATION_COMPLETION)}>
            <Stack direction={"column"} margin={2} spacing={2} justifyItems={"center"} alignItems={"center"} >
                <CircularProgress size={80} />
                <Typography variant={"body2"}>
                    {t(retrying
                        ? SubmissionTranslKey.AUTHORIZATION_SERVICE_TEMPORARILY_UNAVAILABLE_RETRYING
                        : SubmissionTranslKey.AUTHORIZATION_COMPLETION_IN_PROGRESS)}
                </Typography>
            </Stack>
        </Page>
    );
}
